import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import styles from './BottomDrawer.module.scss';

export interface BottomDrawerProps {
    open: boolean;
    onClose: () => void;
    /** Title shown in the drawer header. */
    title?: React.ReactNode;
    /** Right-aligned slot in the header (actions, save button, etc). */
    headerRight?: React.ReactNode;
    /**
     * Drawer height as a CSS value. Defaults to 90vh so it leaves a slim peek
     * of the underlying page (Notion / Linear style).
     */
    height?: string;
    children: React.ReactNode;
    /** Optional class for the drawer surface. */
    className?: string;
    /** Optional class to override / extend the body area (e.g. remove default
     *  padding so the contents can own their own scroll). */
    bodyClassName?: string;
    /** Optional id for aria-labelledby etc. */
    id?: string;
    /** Optional slot rendered as a sibling to the right of (header + body),
     *  spanning the full drawer height. Pushes the main column leftward when
     *  present. Used for side-drawers like version history. */
    rightSlot?: React.ReactNode;
}

export function BottomDrawer({
    open,
    onClose,
    title,
    headerRight,
    height = '90vh',
    children,
    className,
    bodyClassName,
    id,
    rightSlot,
}: BottomDrawerProps) {
    // Lock body scroll while the drawer is open so the content scroll lives
    // inside the drawer panel.
    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = prev;
        };
    }, [open]);

    // Esc closes.
    useEffect(() => {
        if (!open) return;
        function handleKey(e: any) {
            if (e.key === 'Escape') onClose();
        }
        document.addEventListener('keydown', handleKey);
        return () => document.removeEventListener('keydown', handleKey);
    }, [open, onClose]);

    if (typeof document === 'undefined') return null;

    return createPortal(
        <div className={clsx(styles.root, open && styles.rootOpen)} aria-hidden={!open}>
            <div className={styles.backdrop} onMouseDown={onClose} />
            <div
                className={clsx(styles.panel, className)}
                role="dialog"
                aria-modal="true"
                aria-labelledby={id ? `${id}-title` : undefined}
                style={{ height }}
            >
                <div className={styles.mainCol}>
                    <div className={styles.header}>
                        <div className={styles.headerRow}>
                            <div className={styles.title} id={id ? `${id}-title` : undefined}>
                                {title}
                            </div>
                            <div className={styles.headerRight}>
                                {headerRight}
                                <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
                                    ×
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className={clsx(styles.body, bodyClassName)}>{children}</div>
                </div>
                {rightSlot}
            </div>
        </div>,
        document.body,
    );
}
